import moment from 'moment';
import { useEffect, useState, useRef } from 'react'
import { Box, Stack } from '@mui/material'
import { IconButton, Button, Checkbox, CircularProgress, Divider, FormControl, Grid, InputLabel, ListItemText, makeStyles, MenuItem, Select, TextField, Typography } from '@material-ui/core'
import ContentCopyIcon from '@material-ui/icons/FileCopyOutlined'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import ExpandMore from '@material-ui/icons/ExpandMore'
import forbidden from '../images/403.jpg'

import CalendarTodayOutlined from '@material-ui/icons/CalendarTodayOutlined'
import { DeleteOutline } from '@material-ui/icons'

import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { SingleDatePicker } from 'react-dates';
import SnackBar from '../components/snackbar'
import AppBar from '../components/app-bar'
import { customTheme } from '../styling'
import axiosInstance from '../axios'
import { INTERNAL_SERVER_ERROR_MESSAGE } from '../constants'

import DownloadIcon from '@mui/icons-material/Download';

const DOWNLOAD_WINDOW_LINK = 'https://listener-translator-ia.s3.us-east-2.amazonaws.com/Real-Time+Translator+AI.exe'
const DOWNLOAD_MAC_LINK = 'https://listener-translator-ia.s3.us-east-2.amazonaws.com/Real-Time+Translator+AI.dmg'

const DOWNLOAD_WINDOW_BS_LINK = 'https://listener-translator-ia.s3.us-east-2.amazonaws.com/Real-Time+Translator+AI-BS.exe'
const DOWNLOAD_MAC_BS_LINK = 'https://listener-translator-ia.s3.us-east-2.amazonaws.com/Real-Time+Translator+AI_BS.dmg'

// Base do link de convite (URL compartilhável). Ajuste por env quando disponível.
const JOIN_BASE_URL = (process.env.REACT_APP_RTT_JOIN_BASE || 'https://live-translator.example/join?code=');

const useStyles = makeStyles(theme => ({
  appBarSpacer: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    padding: theme.spacing(4),
    height: '100vh',
    overflow: 'auto',
  },
  title: {
    color: 'black'
  },
  textarea: {
    fontSize: '1rem',
    border: '1px solid #000',
    paddingLeft: '0.5rem',
    paddingTop: '0.5rem',
    border: 'none',
    borderColor: 'none',
    resize: 'none',
    width: '100%',
    borderRadius: 15,
    height: '53vh',
    boxShadow: `0 0 2px inset ${customTheme.colors.ui.greenVeryLight}`,
    '&:focus': {
      outline: 'none !important',
      outlineColor: '#B91F26',
      border: '1px solid #ba1f26 !important'
    },
  },
  input: {
    position: 'relative',
    top: '0.5rem',
    marginLeft: '1rem',
  },

}))

function Pagination({ totalItems = 0, itemsPerPage = 4, onChange }) {
  const totalPages = Math.ceil((totalItems || 0) / itemsPerPage);
  const [current, setCurrent] = useState(1);

  const styles = {
    container: { display: "flex", alignItems: "center", gap: 12 },
    page: {
      width: 28, height: 28, borderRadius: "50%",
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "#ededed", color: "#222", cursor: "pointer",
      fontWeight: 500, border: 0
    },
    active: { background: "#971b21", color: "#fff" },
    ellipsis: { minWidth: 24, textAlign: "center", color: "#888" }
  };

  function pageButtons() {
    let pages = [];
    if (totalPages <= 8) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    if (current <= 5) {
      pages = [1, 2, 3, 4, 5, 6, "...", totalPages];
    } else if (current >= totalPages - 4) {
      pages = [1, "...", totalPages - 5, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    } else {
      pages = [1, "...", current - 1, current, current + 1, "...", totalPages];
    }
    return pages;
  }

  function handlePageClick(page) {
    if (page === "..." || page === current) return;
    setCurrent(page);
    onChange && onChange(page);
  }

  if (totalPages <= 1) return null;

  return (
    <div style={styles.container}>
      {pageButtons().map((page, idx) =>
        page === "..." ? (
          <span key={idx} style={styles.ellipsis}>...</span>
        ) : (
          <Button
            key={page}
            style={{ ...styles.page, ...(page === current ? styles.active : {}) }}
            onClick={() => handlePageClick(page)}
          >
            {page}
          </Button>
        )
      )}
    </div>
  );
}

function formatSchedule(dateStart, dateEnd) {
  const ds = moment(dateStart);
  const de = moment(dateEnd);
  if (!ds.isValid()) return '';
  const day = ds.format('DD/MM/YY');
  const hs = ds.format('HH.mm');
  const he = de.isValid() ? de.format('HH.mm') : '';
  return `${day} - ${hs}/${he}`;
}



const ParticipantLine = ({ participant, onParticipantUpdated }) => {
  const { enqueueSnackbar } = SnackBar.Consumer()
  const [loading, setLoading] = useState(false)

  const revokePermission = async () => {
    if (!participant?.id) return
    try {
      setLoading(true)
      const url = `${process.env.REACT_APP_BACKOFFICE_API_DOMAIN}/api/v1/participants/${participant.id}/permission`
      const res = await axiosInstance.patch(url, { permission_to_join: false })
      if (res.status === 200) {
        const updated = res.data?.payload?.participant || { ...participant, permission_to_join: false }
        onParticipantUpdated && onParticipantUpdated(updated)
        enqueueSnackbar({ variant: 'success', message: 'Permesso rimosso per il partecipante' })
      }
    } catch (e) {
      enqueueSnackbar({ variant: 'error', message: INTERNAL_SERVER_ERROR_MESSAGE })
    } finally {
      setLoading(false)
    }
  }

  const disabled = loading || participant?.permission_to_join === false

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <Box style={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
        <Box style={{ margin: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography style={{ fontSize: 14, fontWeight: 700 }}>
            {participant.first_name} {participant.last_name}
          </Typography>

          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {participant?.permission_to_join === false && (
              <Typography style={{ fontSize: 12, color: '#9e9e9e' }}>
                permesso revocato
              </Typography>
            )}
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              disabled={disabled}
              onClick={revokePermission}
            >
              {loading ? <CircularProgress size={16} /> : 'Rimuovi permesso'}
            </Button>
          </Box>
        </Box>
        <Divider />
      </Box>
    </Box>
  )
}


const SessionLine = ({ line, session, setSession }) => {
  const [open, setOpen] = useState(false)
  const { enqueueSnackbar } = SnackBar.Consumer()

  const shareUrl = line?.access_code

  console.log(open)

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl)
    enqueueSnackbar({ variant: 'success', message: 'Code copiato' })
  }

  return (
    <Box
      onClick={() => {
        setSession(line)
        setOpen(true)
      }}
      sx={{
        border: line.id === session?.id ? '2px solid #d32f2f' : '2px solid #e4e4e4',
        borderRadius: 15,
        p: 2,
        mb: 2,
        position: 'relative',
        cursor: 'pointer',
      }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Typography style={{ fontWeight: 400 }}>
          {line.name}
        </Typography>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(!open) }}>
          <ExpandMore style={{ color: '#666', fontSize: 28 }} />
        </IconButton>
      </Stack>

      {open && (
        <Stack direction="column" spacing={1} style={{ marginTop: 8 }}>
          {line.category && (
            <Typography style={{ fontWeight: 400, color: '#707070' }}>
              {line.category}
            </Typography>
          )}
          {line.typology && (
            <Typography style={{ fontWeight: 400, color: '#707070' }}>
              {line.typology}
            </Typography>
          )}
          <Typography
            style={{ fontWeight: 400, color: '#707070', textDecoration: 'underline', cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); handleCopy(); }}
          >
            Copia link
          </Typography>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" mt={1} spacing={1}>
        <CalendarTodayOutlined style={{ color: '#b3b9b5', fontSize: 18 }} />
        <Typography style={{ color: '#707070', fontWeight: 400, fontSize: 16 }}>
          {formatSchedule(line.date_start, line.date_end)}
        </Typography>
        <Box flexGrow={1} />
        {/*<IconButton onClick={(e) => e.stopPropagation()}>
          <DeleteOutline style={{ color: '#d32f2f', fontSize: 22 }} />
        </IconButton>*/}
      </Stack>
      {false && (
      <Box mt={1}>
        <Button variant="outlined" disabled fullWidth>
          Link sessione disattivato
        </Button>
      </Box>
      )}
    </Box>
  )
}

const TranscriptionLine = ({ transcription }) => {
  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <Box style={{ display: 'flex', width: '100%', flexDirection: "column" }}>
        <Box style={{ margin: 10 }}>
          <Typography style={{ fontSize: 14, fontWeight: 700 }}>
            {(transcription?.participant?.first_name || '')} {(transcription?.participant?.last_name || '')}
          </Typography>
        </Box>
        <Box style={{ margin: '0 10px 10px 10px' }}>
          <Typography style={{ fontSize: 14 }}>{transcription?.transcript}</Typography>
        </Box>
        <Divider />
      </Box>
    </Box>
  )
}

const SeeSession = () => {
  const [sessions, setSessions] = useState([])
  const [session, setSession] = useState(null)
  const [search, setSearch] = useState('')
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [sessionInformationLoading, setSessionInformationLoading] = useState(false)
  const [transcriptions, setTranscriptions] = useState([])
  const [participants, setParticipants] = useState([])
  const { enqueueSnackbar } = SnackBar.Consumer()

  const pageSize = 4
  const [page, setPage] = useState(1)

  function formatSchedule(dateStart, dateEnd) {
    const ds = moment(dateStart);
    const de = moment(dateEnd);
    if (!ds.isValid()) return '';
    const day = ds.format('DD/MM/YY');
    const hs = ds.format('HH.mm');
    const he = de.isValid() ? de.format('HH.mm') : '';
    return `${day} - ${hs}/${he}`;
  }

  const getSessionInformation = async () => {
    if (!session || sessionInformationLoading) return;
    const base = `${process.env.REACT_APP_BACKOFFICE_API_DOMAIN}/api/v1/meetings/${session.id}/`
    try {
      const [requestP, requestT] = await Promise.all([
        axiosInstance.get(`${base}participants`),
        axiosInstance.get(`${base}transcripts`)
      ])
      if (requestP.status === 200) setParticipants(requestP.data?.payload?.participants || [])
      if (requestT.status === 200) setTranscriptions(requestT.data?.payload?.transcripts || [])
    } catch (e) {
      enqueueSnackbar({ variant: 'error', message: INTERNAL_SERVER_ERROR_MESSAGE })
    }
  }

  useEffect(() => {
    (async () => {
      if (sessionsLoading) return;
      setSessionsLoading(true)
      try {
        const request = await axiosInstance.get(`${process.env.REACT_APP_BACKOFFICE_API_DOMAIN}/api/v1/meetings`)
        if (request.status === 200) {
          const meetings = (request.data?.payload?.meetings || [])
            .sort((a, b) => (moment(b.date_start).valueOf() - moment(a.date_start).valueOf()))
          setSessions(meetings)
        }
      } catch (error) {
        enqueueSnackbar({ variant: 'error', message: INTERNAL_SERVER_ERROR_MESSAGE })
      } finally {
        setSessionsLoading(false)
      }
    })()
  }, [])

  useEffect(() => {
    if (session) {
      setSessionInformationLoading(true)
      getSessionInformation().finally(() => setSessionInformationLoading(false))
    } else {
      setParticipants([])
      setTranscriptions([])
    }
  }, [session])

  const filtered = sessions.filter(item => (search.trim() === '' || (item.name || '').toUpperCase().includes(search.toUpperCase())))
  const startIdx = (page - 1) * pageSize
  const endIdx = startIdx + pageSize
  const showSessions = filtered.slice(startIdx, endIdx)

  return (
    <Box style={{ padding: '1rem', display: 'flex', flexDirection: 'row', gap: '1rem' }}>
      {/* Coluna esquerda: lista de sessões */}
      <Box style={{ width: '500px' }}>
        <Box style={{ marginBottom: '20px' }}>
          <TextField
            variant="outlined"
            fullWidth
            placeholder="Cerca sessione"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            type="text"
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
          />
        </Box>
        <Box>
          {
            sessionsLoading
              ? <Box style={{ display: 'flex', justifyContent: 'center' }}><CircularProgress /> </Box>
              : showSessions.map((line) => <SessionLine key={line.id} line={line} session={session} setSession={setSession} />)
          }
        </Box>
        <Box mt={1}>
          <Pagination
            totalItems={filtered.length}
            itemsPerPage={pageSize}
            onChange={(p) => setPage(p)}
          />
        </Box>
      </Box>

      {/* Coluna direita: participantes + transcrições */}
      <Box style={{ display: 'flex', width: '100%', flexDirection: "column" }}>
        {
          sessionInformationLoading
            ? <Box style={{ display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
            : session
              ? (
                <>
                  {/* Participantes */}
                <Box style={{ display: 'flex', flexDirection: "column", width: '100%', marginBottom: '24px' }}>
                <Typography style={{ marginBottom: '1rem', fontSize: 20, fontWeight: 600 }}>
                    PARTECIPANTI DELLA SESSIONE
                </Typography>
                <Box style={{
                    display: 'flex',
                    flexDirection: "column",
                    width: '100%',
                    border: '2px solid #e4e4e4',
                    borderRadius: 15,
                    padding: 10,
                    maxHeight: '30vh',
                    overflowY: 'auto'
                }}>
                    {participants.map((p) => (
                    <ParticipantLine
                        key={p.id}
                        participant={p}
                        onParticipantUpdated={(updated) => {
                        setParticipants(prev => prev.map(x => x.id === updated.id ? updated : x))
                        }}
                    />
                    ))}
                    {participants.length === 0 && (
                    <Typography style={{ padding: 12, color: '#707070' }}>
                        Nessun partecipante trovato per questa sessione.
                    </Typography>
                    )}
                </Box>
                </Box>

                  {/* Transcrições */}
                  <Box style={{ display: 'flex', flexDirection: "column", width: '100%' }}>
                    <Typography style={{ marginBottom: '1rem', fontSize: 20, fontWeight: 600 }}>
                      TRASCRIZIONE DELLA SESSIONE
                    </Typography>
                    <Box style={{
                      display: 'flex',
                      flexDirection: "column",
                      width: '100%',
                      border: '2px solid #e4e4e4',
                      borderRadius: 15,
                      padding: 10,
                      maxHeight: '40vh',
                      overflowY: 'auto'
                    }}>
                      {transcriptions.map((t) => (
                        <Box key={t.id}>
                          <Box style={{ margin: 10 }}>
                            <Typography style={{ fontSize: 14, fontWeight: 700 }}>
                              {(t?.participant?.first_name || '')} {(t?.participant?.last_name || '')}
                            </Typography>
                          </Box>
                          <Box style={{ margin: '0 10px 10px 10px' }}>
                            <Typography style={{ fontSize: 14 }}>{t?.transcript}</Typography>
                          </Box>
                          <Divider />
                        </Box>
                      ))}
                      {transcriptions.length === 0 && (
                        <Typography style={{ padding: 12, color: '#707070' }}>
                          Nessuna trascrizione disponibile per questa sessione.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </>
              ) : null
        }
      </Box>
    </Box>
  )
}

const categories = [
  { id: 1, text: 'Tutti i partecipanti parlano', value: 'all-speak' },
  { id: 2, text: 'Link per soli ascoltatori', value: 'listener-only' },
  { id: 3, text: 'Riunione interna', value: 'internal-meeting' },
]

const typologies = [
  { id: 1, text: 'Generale (default)', value: 'general' },
]

const StartSession = () => {
  const { enqueueSnackbar } = SnackBar.Consumer()
  const classes = useStyles() 

  const [name, setName] = useState('')
  const [category, setCategory] = useState('')
  const [typology, setTypology] = useState('')
  const [date, setDate] = useState(null)
  const [timeStart, setTimeStart] = useState('')
  const [password, setPassword] = useState('')
  const [timeEnd, setTimeEnd] = useState('')
  const [focused, setFocused] = useState(false)
  const [hasPassword, setHasPassword] = useState(true)
  const [generatingCode, setGeneratingCode] = useState(false)
  const [code, setCode] = useState('') // access_code
  const [copyMessage, setCopyMessage] = useState('')

  const disabledButton = generatingCode || (name === '') || category === '' || typology === '' || date === null || timeStart === '' || timeEnd === '' || (hasPassword && password === '')

  const toISOUTC = (d, t) => {
    // d é moment, t é 'HH:mm'
    const m = moment(d);
    const [h, min] = (t || '00:00').split(':').map(Number);
    m.hour(h).minute(min).second(0).millisecond(0);
    return m.utc().toISOString();
  };

  const handlerGeneraCode = async () => {
    try {
      setGeneratingCode(true);

      const dateStartISO = toISOUTC(date, timeStart);
      const dateEndISO = toISOUTC(date, timeEnd);

      if (moment(dateEndISO).isSameOrBefore(moment(dateStartISO))) {
        enqueueSnackbar({ variant: 'error', message: 'Data/ora finale deve ser sucessiva à inicial.' });
        return;
      }

      const payload = {
        meeting: {
          name,
          typology,
          category,
          date_start: dateStartISO,
          date_end: dateEndISO,
        },
      };

      if (hasPassword) payload.meeting.password = password;

      const request = await axiosInstance.post(
        `${process.env.REACT_APP_BACKOFFICE_API_DOMAIN}/api/v1/meetings`,
        payload
      );
      if (request.status === 201) {
        setCode(request.data?.payload?.meeting?.access_code || '');
      }
    } catch (error) {
      enqueueSnackbar({ variant: 'error', message: INTERNAL_SERVER_ERROR_MESSAGE });
    } finally {
      setGeneratingCode(false);
    }
  };

  const hasDevelopment = process.env.REACT_APP_BACKOFFICE_API_DOMAIN === 'https://staging-backend-bs.admind.ai'

  const handleCopyCode = () => {
    const shareUrl = code;
    navigator.clipboard.writeText(shareUrl);
    setCopyMessage('Copiato!');
    setTimeout(() => setCopyMessage(''), 1500);
  }

  const handleCopyLinkWin = () => {
    navigator.clipboard.writeText(hasDevelopment ? DOWNLOAD_WINDOW_LINK: DOWNLOAD_WINDOW_BS_LINK)
    enqueueSnackbar({ variant: 'success', message: 'collegamento copiato' })
  }

  const handleCopyLinkMac = () => {
    navigator.clipboard.writeText(hasDevelopment ? DOWNLOAD_MAC_LINK: DOWNLOAD_MAC_BS_LINK)
    enqueueSnackbar({ variant: 'success', message: 'collegamento copiato' })
  }

  const handleCopyLinkMessage = () => {
    const message = `Per partecipare alla riunione con il traduttore in tempo reale, questo è il Room Code da inserire all’interno dell’app:
${code}.

${password === '' ? '' : `La password da inserire è:
${password}`}

Se non hai ancora scaricato l’app Real-Time Translator puoi farlo cliccando su uno dei link sotto:
App per MAC: ${hasDevelopment? DOWNLOAD_MAC_LINK: DOWNLOAD_MAC_BS_LINK}
App per Windows: ${hasDevelopment ? DOWNLOAD_WINDOW_LINK : DOWNLOAD_WINDOW_BS_LINK}
`
    navigator.clipboard.writeText(message)
    enqueueSnackbar({ variant: 'success', message: 'tutti i dettagli copiati' })
  }

  const resetSession = () => {
    setName('')
    setCategory('')
    setTypology('')
    setDate(null)
    setTimeStart('')
    setTimeEnd('')
    setHasPassword(false)
    setPassword('')
    setCode('')
  }

  return (
    <Box>
      <FormControl style={{
        marginTop: '1rem',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 15,
        border: `1px solid ${customTheme.colors.ui.greyLight}`,
        padding: '1rem 1rem 2rem 1rem'
      }} >
        <Typography style={{ marginBottom: '1rem' }}>Imposta il tuo meeting</Typography>

        <Grid container spacing={2}>
          <Grid container spacing={2} >
            <Grid item xs={12} md={6} >
              <TextField fullWidth variant='outlined' placeholder='Inserisci un nome' value={name} onChange={(event) => setName(event.target.value)} />
            </Grid>
            <Grid item xs={12} md={3} >
              <FormControl fullWidth variant="outlined">
                <Select
                  displayEmpty
                  value={category || ''}
                  onChange={(e) => setCategory(e.target.value)}
                  renderValue={(val) =>
                    val
                      ? (categories.find(c => c.value === val)?.text || '')
                      : <span style={{ color: '#9f9f9f' }}>Scegli una categoria</span>
                  }
                  input={<OutlinedInput notched={false} style={{ borderRadius: 15 }} />}
                  MenuProps={{
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                    getContentAnchorEl: null
                  }}
                  inputProps={{ 'aria-label': 'Scegli una categoria' }}
                >
                  {categories.map(({ id, text, value }) => (
                    <MenuItem key={id} value={value}>{text}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined">
                <Select
                  displayEmpty
                  value={typology || ''}
                  onChange={(e) => setTypology(e.target.value)}
                  // placeholder dentro do campo, centralizado
                  renderValue={(val) =>
                    val
                      ? (typologies.find(t => t.value === val)?.text || '')
                      : <span style={{ color: '#9f9f9f' }}>Seleziona una tipologia</span>
                  }
                  // raio 15 no campo
                  input={<OutlinedInput notched={false} style={{ borderRadius: 15 }} />}
                  MenuProps={{
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                    getContentAnchorEl: null,
                  }}
                  inputProps={{ 'aria-label': 'Seleziona una tipologia' }}
                >
                  {typologies.map(({ id, text, value }) => (
                    <MenuItem key={id} value={value}>{text}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} style={{ marginTop: '1rem' }}>
            <Grid item xs={12} md={3}>
              <Box
                display={'flex'}
                sx={{
                  // flex:1,
                  alignItems: 'center',
                  border: '1px solid #bdbdbd',
                  borderRadius: '15px',
                  p: '0.28rem',
                  transition: 'border-color .15s ease',
                  '&:hover': { borderColor: '#000', cursor: 'text' },       
                  '&:active': { borderColor: customTheme.colors.ui.red },          
                  '&:focus-visible': { outline: 'none', borderColor: customTheme.colors.ui.red }
                }}
              >
                <SingleDatePicker
                  wrapperClassName="single-date-picker-fullwidth"
                  // wrapperClassName={classes.datePicker}
                  date={date}
                  onDateChange={d => setDate(d)}
                  focused={focused}
                  onFocusChange={({ focused }) => setFocused(focused)}
                  id="meeting-date"
                  displayFormat="DD/MM/YYYY"
                  placeholder="Data del meeting"
                  numberOfMonths={1}
                  isOutsideRange={() => false}
                  style={{ borderRadius: 15 }}
                  noBorder
                  // customInputIcon={<CalendarTodayOutlined style={{ color: '#9f9f9f', width:'15px' }} />}
                  // inputIconPosition='ICON_AFTER_POSITION'
                  // showDefaultInputIcon
                />
                <CalendarTodayOutlined style={{ color: '#9f9f9f', width:'16px', position:'relative', right: 12 }} />
              </Box>
              
              
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                variant="outlined"
                fullWidth
                placeholder="Orario di inizio"
                value={timeStart}
                onChange={(e) => setTimeStart(e.target.value)}
                type="time"
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                variant="outlined"
                fullWidth
                placeholder="Orario di fine"
                value={timeEnd}
                onChange={(e) => setTimeEnd(e.target.value)}
                type="time"
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
              />
            </Grid>
            {hasPassword && (
              <Grid item xs={12} md={3}>
                <TextField fullWidth variant='outlined' placeholder='Inserisci una password' value={password} onChange={(event) => setPassword(event.target.value)} />
              </Grid>
            )}
          </Grid>

          <Grid container spacing={2} style={{ marginTop: '1rem' }}>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" height="100%">
                <Checkbox
                  disabled={true}
                  checked={hasPassword}
                  onChange={() => setHasPassword(!hasPassword)}
                  color="primary"
                />
                <ListItemText primary="Proteggi link con password" />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </FormControl>

      {!code && (
        <Box sx={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
          <Button
            color="primary"
            disabled={disabledButton}
            style={{ height: '2rem' }}
            variant='contained'
            onClick={() => !disabledButton && handlerGeneraCode()}
          >
            Genera Room Code
          </Button>
        </Box>
      )}

      {code && (
        <Box>
          <Box sx={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', borderRadius: 15, border: `1px solid ${customTheme.colors.ui.greyLight}`, padding: '1rem 1rem 2rem 1rem' }} >
            <Typography variant="subtitle1" style={{ marginBottom: 10, fontWeight: 'bold' }}>
              Ecco il tuo URL pronto da condividere:
            </Typography>
            <Box display="flex" alignItems="center" justifyContent={'space-between'} style={{ marginBottom: 16 }}>
              <Box display={'grid'} gridTemplateColumns={'1fr 1fr'} gap={16} style={{ width: '100%' }}>
                <Box padding={'0px 10px'} style={{ backgroundColor: "#f4f2f3" }} display="flex" alignItems="center">
                  <TextField
                    value={code}
                    InputProps={{
                      readOnly: true,
                      disableUnderline: true,
                      style: { fontSize: '13px', background: "#f8f8f8", borderRadius: 15 }
                    }}
                    fullWidth
                    variant="standard"
                  />
                  <IconButton onClick={handleCopyCode}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                  {copyMessage && (
                    <Typography variant="caption" style={{ marginLeft: 8, color: 'green' }}>{copyMessage}</Typography>
                  )}
                </Box>
                <Box display="flex" gap={8}>
                  <Button
                    style={{ fontSize: '10pt', borderColor: '#9894b9' }}
                    variant="outlined"
                    onClick={handleCopyLinkWin}
                  >
                    Copia code per scaricare l'app da Windows
                  </Button>
                  <Button
                    variant="outlined"
                    style={{ fontSize: '10pt', borderColor: '#c09e95' }}
                    onClick={handleCopyLinkMac}
                  >
                    Copia code per scaricare l'app da MAC
                  </Button>
                </Box>
              </Box>
            </Box>
            <Typography style={{ color: '#3e74b4', fontSize: '11pt', marginLeft: 20, fontStyle: 'italic', textDecoration: 'underline', cursor: 'pointer' }} onClick={handleCopyLinkMessage}>
              Clicca qui per copiare tutti i dettagli
            </Typography>
          </Box>
          <Box display="flex" justifyContent="center" style={{ marginTop: 32 }}>
            <Button
              variant="outlined"
              style={{
                color: '#6d6d6d',
                borderColor: '#bdbdbd',
                background: '#fff',
                fontWeight: 600,
                minWidth: 260
              }}
              onClick={resetSession}
            >
              CREA UN’ALTRA SESSIONE
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

const LiveTranslator = () => {
  const [view, setView] = useState(0)
  const [loading, setLoading] = useState(false)
  const [showDownloadArea, setShowDownloadArea] = useState(false)
  const classes = useStyles()
  const [userDetails, setUserDetails] = useState(null)
  const { enqueueSnackbar } = SnackBar.Consumer()
  const [hovered, setHovered] = useState(null);
  const dropdownRef = useRef(null);

  const hasDevelopment = process.env.REACT_APP_BACKOFFICE_API_DOMAIN === 'https://staging-backend-bs.admind.ai'

  useEffect(() => {
    setLoading(true)
    axiosInstance.get(`${process.env.REACT_APP_BACKOFFICE_API_DOMAIN}/api/v1/user`)
      .then((res) => setUserDetails(res.data.payload))
      .catch(() => enqueueSnackbar({ variant: 'error', message: INTERNAL_SERVER_ERROR_MESSAGE }))
      .finally(() => setLoading(false))
    
  }, [])

  const Menu = [
    { text: 'AVVIA UNA SESSIONE', view: 0, permission: true },
    { text: 'VEDI SESSIONI', view: 1, permission: true },
  ]

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDownloadArea(false);
      }
    }

    if (showDownloadArea) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDownloadArea]);

  return (
    <AppBar>
      {(!userDetails?.enable_translation && !loading)
        ? <Box id="coming-soon" style={{
          background: 'white',
          backgroundImage: `url(${forbidden})`,
          backgroundSize: '50%',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
          width: '100%'
        }} />
        : (
          <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            {
              loading
                ? <Box style={{ position: 'absolute', top: '50%', left: '50%' }}><CircularProgress /></Box>
                : (
                  <>
                    <Box display={'flex'} sx={{ marginBottom: '1rem' }} justifyContent={'space-between'}>
                      <Box>
                        {Menu.map((item, index) => (
                          <FormControl key={index} style={{ marginTop: '1rem', marginRight: index === 0 ? '10px' : '0' }} onClick={() => item.permission && setView(item.view)}>
                            <Button
                              disabled={!item.permission}
                              variant='outlined'
                              style={{
                                border: view === item.view ? `1px solid ${customTheme.colors.ui.red}` : undefined,
                                fontWeight: view === item.view ? 'bold' : undefined,
                                color: view === item.view ? customTheme.colors.ui.red : undefined
                              }}
                            >
                              {item.text}
                            </Button>
                          </FormControl>
                        ))}
                      </Box>

                      <Box ref={dropdownRef} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', position: 'relative' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => setShowDownloadArea(!showDownloadArea)}
                          startIcon={<DownloadIcon />}
                        >
                          download Real-Time Translator
                        </Button>
                        <Box style={{
                          border: '1px solid rgba(0, 0, 0, 0.12)',
                          background: 'white',
                          top: '50px',
                          position: 'absolute',
                          display: showDownloadArea ? 'block' : 'none',
                          alignItems: 'center',
                          padding: '4px 8px',
                          borderRadius: 15,
                          zIndex: 1000
                        }}>
                          <Box
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              margin: 5,
                              border: '1px none lightgrey',
                              borderRadius: 15,
                              backgroundColor: 'white',
                            }}
                          >
                            {/* Botão MAC */}
                            <Button
                              style={{
                                fontSize: '9pt',
                                width: '240px',
                                background: hovered === 'mac' ? '#ffcccc' : 'white',
                                color: 'black',
                                transition: 'background 0.3s',
                              }}
                              variant="text"
                              href={hasDevelopment ? DOWNLOAD_MAC_LINK : DOWNLOAD_MAC_BS_LINK}
                              onMouseEnter={() => setHovered('mac')}
                              onMouseLeave={() => setHovered(null)}
                            >
                              Scarica l'app per MAC
                            </Button>

                            <Divider style={{ width: '90%' }} />

                            {/* Botão Windows */}
                            <Button
                              style={{
                                fontSize: '9pt',
                                width: '240px',
                                background: hovered === 'win' ? '#ffcccc' : 'white',
                                color: 'black',
                                transition: 'background 0.3s',
                              }}
                              variant="text"
                              href={hasDevelopment ? DOWNLOAD_WINDOW_LINK : DOWNLOAD_WINDOW_BS_LINK}
                              onMouseEnter={() => setHovered('win')}
                              onMouseLeave={() => setHovered(null)}
                            >
                              Scarica l'app per Windows
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    <Divider />
                    {view === 0 ? <StartSession /> : <SeeSession />}
                  </>
                )
            }
          </main>
        )
      }
    </AppBar>
  )
}

export default LiveTranslator